<template>
  <div class="community-discussion-listing-detail pa-5">
    <article>
      <v-row>
        <v-col cols="auto">
          <CommunityDiscussionVote
            :is-logged-in="isLoggedIn"
            :has-user-voted="hasUserVoted"
            :votes-count="votesCount"
            :title="`discussion ${title}`"
            @voted="handleDiscussionVoted"
            @unvoted="handleDiscussionUnvoted"
          />
        </v-col>
        <v-col>
          <div
            class="d-flex align-center justify-space-between muted--text text-body-2"
          >
            <div class="d-flex align-center">
              <div
                :class="`community-discussion-listing-detail__pill-${type.toLowerCase()}`"
                class="text-capitalize py-1 px-2 mr-4 font-weight-medium rounded-4 text-caption"
              >
                {{ type }}
              </div>
              <span class="font-weight-medium">{{ authorName }}</span>
            </div>
            <span class="d-none d-sm-inline">{{ date | localDateTime }}</span>
            <span class="d-sm-none">{{ dateMobileView }}</span>
          </div>
          <nuxt-link :to="goToDiscussionDetails" class="text-decoration-none">
            <h3
              class="community-discussion-listing-detail__title accent--text font-weight-medium pb-3 pt-2"
            >
              {{ title }}
            </h3>
          </nuxt-link>
          <p
            class="community-discussion-listing-detail__description text-body-2"
          >
            {{ descriptionToDisplay }}
          </p>
          <div class="d-flex text-caption font-weight-bold">
            <div class="d-flex ml-md-3">
              <img alt="" src="~/static/images/Reply.svg" class="mr-2" />
              <span class="width-96">{{ repliesCount }} Replies</span>
            </div>
          </div>
        </v-col>
      </v-row>
    </article>
  </div>
</template>

<script>
import PQueue from "p-queue";

import CommunityDiscussionVote from "~/components/communities/discussion/CommunityDiscussionVote";
import localDateTime from "~/plugins/utilities/localDateTime";
import isLoggedIn from "~/plugins/utilities/isLoggedIn";
import {
  gtmEventCategories,
  gtmEventActions,
} from "~/components/utilities/gtmEventEnums";

const MAX_NUMBER_OF_WORDS = 100;

export default {
  name: "CommunityDiscussionListingDetail",
  components: { CommunityDiscussionVote },
  props: {
    author: { type: Object, default: () => ({}) },
    community: { type: Object, default: () => ({}) },
    date: { type: String, required: true },
    description: { type: String, required: true },
    id: { type: String, required: true },
    title: { type: String, required: true },
    type: { type: String, required: true },
    repliesCount: { type: Number, required: true },
    votesCount: { type: Number, required: true },
    hasUserVoted: { type: Boolean, required: true },
  },
  computed: {
    partner() {
      return this.$config.partner;
    },
    dateMobileView() {
      return localDateTime(this.date, "MMM yyyy");
    },
    authorName() {
      const { title, firstName, lastName } = this.author;
      return `${title} ${firstName} ${lastName}`;
    },
    descriptionToDisplay() {
      const text = this.description.trim();

      const words = text.match(/\s*\S+/gim);

      if (words.length > MAX_NUMBER_OF_WORDS) {
        const shortDescription = words.slice(0, MAX_NUMBER_OF_WORDS);

        return `${shortDescription.join(" ")}...`;
      }

      return this.description;
    },
    goToDiscussionDetails() {
      return `/engage/${this.partner}/community-details/${this.community.id}/discussion/${this.id}`;
    },
    user() {
      return this.$store.state.user;
    },
    isLoggedIn() {
      return isLoggedIn(this.user);
    },
  },
  created() {
    this.voteQueue = new PQueue({ concurrency: 1 });
  },
  methods: {
    handleDiscussionVoted() {
      this.voteQueue.add(async () => {
        await this.$store.dispatch("createDiscussionVote", {
          discussionId: this.id,
        });
        this.sendGtmEvent();
      });
    },
    handleDiscussionUnvoted() {
      this.voteQueue.add(async () => {
        await this.$store.dispatch("removeDiscussionVote", {
          discussionId: this.id,
        });
        this.sendGtmEvent();
      });
    },
    sendGtmEvent() {
      this.$gtm.push({
        event: gtmEventActions.CLICK_DISCUSSION_TOPIC_VOTE,
        category: gtmEventCategories.COMMUNITY_DISCUSSIONS,
        label: this.community.name,
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.community-discussion-listing-detail {
  &:not(:last-child) {
    border-bottom: 1px solid var(--v-brighter-base);
  }

  &__pill-announcement {
    background-color: #d4f8d8;
    color: #167a3e;
  }

  &__pill-question {
    background-color: #d4e6f8;
    color: #164a7a;
  }

  &__pill-comment {
    background-color: #f8eed4;
    color: #7a5e16;
  }

  &__title {
    line-height: 21px;
    font-size: 18px;
  }

  &__description {
    white-space: pre-line;
  }
}
</style>
